"use client";

import { useState } from "react";
import Icon from "@/components/ui/AppIcon";
import AppImage from "@/components/ui/AppImage";
import { inquiryService } from "@/lib/services/inquiryService";

type FormData = {
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
  productInterest: string;
  orderVolume: string;
  message: string;
};

const productOptions = [
"CPU 處理器", "GPU 顯示卡", "RAM 記憶體", "Motherboard 主機板",
"Storage 儲存裝置", "Peripherals 週邊設備", "Multiple Categories 多類產品", "Other 其他"];


const volumeOptions = [
"Small (10-50 units)", "Medium (50-200 units)", "Large (200-1000 units)", "Enterprise (1000+ units)"];


export default function ContactContent() {
  const [formData, setFormData] = useState<FormData>({
    companyName: "",
    contactName: "",
    email: "",
    phone: "",
    productInterest: "",
    orderVolume: "",
    message: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    const result = await inquiryService.submit(formData);
    setIsSubmitting(false);
    if (result.success) {
      setSubmitted(true);
    } else {
      setSubmitError(result.error || "Failed to submit enquiry. Please try again.");
    }
  };

  return (
    <section className="py-20 bg-bg-light">
      <div className="max-w-8xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left — Contact Info */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            {/* HK Office Card */}
            <div className="bg-white rounded-4xl p-8 border border-border shadow-navy-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-orange/10 flex items-center justify-center">
                  <Icon name="BuildingOfficeIcon" size={22} className="text-orange" />
                </div>
                <div>
                  <h3 className="font-heading font-black text-navy text-lg">Hong Kong Office</h3>
                  <p className="text-xs text-text-muted">香港辦事處 · Est. 2026</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex gap-3">
                  <Icon name="MapPinIcon" size={18} className="text-orange flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-navy text-sm">Address 地址</p>
                    <p className="text-text-mid text-sm leading-relaxed mt-1">
                      香港觀塘海濱道139號<br />
                      139 Hoi Bun Road, Kwun Tong<br />
                      Kowloon, Hong Kong
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Icon name="DevicePhoneMobileIcon" size={18} className="text-orange flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-navy text-sm">WhatsApp</p>
                    <a
                      href="https://wa.me/85259773386"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-orange font-bold text-lg hover:text-orange-dark transition-colors">
                      
                      +852 5977 3386
                    </a>
                    <p className="text-xs text-text-muted mt-0.5">Fastest response channel · 最快回覆</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Icon name="EnvelopeIcon" size={18} className="text-orange flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-navy text-sm">Email 電郵</p>
                    <a
                      href="mailto:andy1oo5@pccargohk.com"
                      className="text-orange font-bold text-lg hover:text-orange-dark transition-colors">
                      andy1oo5@pccargohk.com
                    </a>
                    <p className="text-xs text-text-muted mt-0.5">Business inquiries · 業務查詢</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Icon name="ClockIcon" size={18} className="text-orange flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-navy text-sm">Business Hours 辦公時間</p>
                    <p className="text-text-mid text-sm mt-1">Mon–Fri: 9:00 AM – 6:00 PM HKT</p>
                    <p className="text-text-muted text-xs">Sat: 9:00 AM – 1:00 PM</p>
                  </div>
                </div>
              </div>

              {/* WhatsApp CTA */}
              <a
                href="https://wa.me/85259773386"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white py-3.5 rounded-2xl font-black text-sm transition-all duration-200 shadow-lg">
                
                <Icon name="ChatBubbleLeftRightIcon" size={16} variant="solid" />
                Open WhatsApp Chat 立即聯絡
              </a>
            </div>

            {/* Taiwan HQ Card */}
            <div className="bg-navy rounded-4xl p-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center">
                  <Icon name="GlobeAsiaAustraliaIcon" size={22} className="text-yellow-brand" />
                </div>
                <div>
                  <h3 className="font-heading font-black text-white text-lg">Taiwan Headquarters</h3>
                  <p className="text-xs text-white/50">台灣總公司</p>
                </div>
              </div>
              <p className="text-white/70 text-sm leading-relaxed mb-5">
                Our headquarters in Taiwan maintains direct relationships with leading PC hardware manufacturers. All sourcing and quality control is managed from Taiwan.
              </p>
              <div className="flex flex-wrap gap-3">
                {["Direct Factory Access", "QC Certified", "10+ Year Relations"].map((tag) =>
                <span key={tag} className="text-xs px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/70 font-semibold">
                    {tag}
                  </span>
                )}
              </div>
            </div>

            {/* Map image placeholder */}
            <div className="overflow-hidden rounded-4xl h-48 relative">
              <AppImage
                src="https://images.unsplash.com/photo-1729675200353-e27a1856c2f3"
                alt="Hong Kong Kowloon city skyline view from waterfront"
                fill
                className="object-cover" />
              
              <div className="absolute inset-0 bg-navy/50 flex items-end p-5">
                <div className="flex items-center gap-2 text-white">
                  <Icon name="MapPinIcon" size={16} className="text-orange" />
                  <span className="text-sm font-bold">Kwun Tong, Kowloon, Hong Kong</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right — Enquiry Form */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-4xl p-8 md:p-10 border border-border shadow-navy-sm">
              {!submitted ?
              <>
                  <h2 className="font-heading font-black text-3xl text-navy mb-2">
                    Send an Enquiry
                  </h2>
                  <p className="text-text-muted text-sm mb-8">
                    Fill in your requirements and we'll prepare a detailed wholesale quotation within 24 hours. 發送您的採購需求，我們將在24小時內回覆報價。
                  </p>

                  <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                    {/* Row 1 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-black text-navy uppercase tracking-wide mb-2">
                          Company Name 公司名稱 *
                        </label>
                        <input
                        type="text"
                        name="companyName"
                        required
                        value={formData.companyName}
                        onChange={handleChange}
                        placeholder="Your Company Ltd."
                        className="input-field w-full bg-bg-light border border-border rounded-xl px-4 py-3 text-sm text-text-dark placeholder:text-text-muted" />
                      
                      </div>
                      <div>
                        <label className="block text-xs font-black text-navy uppercase tracking-wide mb-2">
                          Contact Name 聯絡人 *
                        </label>
                        <input
                        type="text"
                        name="contactName"
                        required
                        value={formData.contactName}
                        onChange={handleChange}
                        placeholder="Your full name"
                        className="input-field w-full bg-bg-light border border-border rounded-xl px-4 py-3 text-sm text-text-dark placeholder:text-text-muted" />
                      
                      </div>
                    </div>

                    {/* Row 2 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-black text-navy uppercase tracking-wide mb-2">
                          Email 電郵 *
                        </label>
                        <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="you@company.com"
                        className="input-field w-full bg-bg-light border border-border rounded-xl px-4 py-3 text-sm text-text-dark placeholder:text-text-muted" />
                      
                      </div>
                      <div>
                        <label className="block text-xs font-black text-navy uppercase tracking-wide mb-2">
                          Phone / WhatsApp
                        </label>
                        <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+852 XXXX XXXX"
                        className="input-field w-full bg-bg-light border border-border rounded-xl px-4 py-3 text-sm text-text-dark placeholder:text-text-muted" />
                      
                      </div>
                    </div>

                    {/* Row 3 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-black text-navy uppercase tracking-wide mb-2">
                          Product Interest 產品類別 *
                        </label>
                        <select
                        name="productInterest"
                        required
                        value={formData.productInterest}
                        onChange={handleChange}
                        className="input-field w-full bg-bg-light border border-border rounded-xl px-4 py-3 text-sm text-text-dark">
                        
                          <option value="">Select category...</option>
                          {productOptions.map((opt) =>
                        <option key={opt} value={opt}>{opt}</option>
                        )}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-black text-navy uppercase tracking-wide mb-2">
                          Order Volume 採購量
                        </label>
                        <select
                        name="orderVolume"
                        value={formData.orderVolume}
                        onChange={handleChange}
                        className="input-field w-full bg-bg-light border border-border rounded-xl px-4 py-3 text-sm text-text-dark">
                        
                          <option value="">Select volume...</option>
                          {volumeOptions.map((opt) =>
                        <option key={opt} value={opt}>{opt}</option>
                        )}
                        </select>
                      </div>
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-xs font-black text-navy uppercase tracking-wide mb-2">
                        Message / BOM List 訊息 / 採購清單 *
                      </label>
                      <textarea
                      name="message"
                      required
                      rows={6}
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Please describe your requirements, specific models needed, quantities, and any other details. You can paste your BOM list here..."
                      className="input-field w-full bg-bg-light border border-border rounded-xl px-4 py-3 text-sm text-text-dark placeholder:text-text-muted resize-none" />
                    
                    </div>

                    {/* Submit */}
                    <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center gap-2 bg-navy hover:bg-navy-dark disabled:opacity-60 disabled:cursor-not-allowed text-white py-4 rounded-2xl font-black text-sm uppercase tracking-wider transition-all duration-200 shadow-navy-md hover:shadow-navy-lg">
                    
                      <Icon name="PaperAirplaneIcon" size={16} />
                      {isSubmitting ? "Submitting... 提交中..." : "Submit Enquiry 提交詢價"}
                    </button>

                    {submitError && (
                      <p className="text-center text-sm text-red-500 font-semibold">{submitError}</p>
                    )}

                    <p className="text-center text-xs text-text-muted">
                      Or contact us directly via{" "}
                      <a href="https://wa.me/85259773386" target="_blank" rel="noopener noreferrer" className="text-orange font-bold hover:underline">
                        WhatsApp +852 5977 3386
                      </a>
                    </p>
                  </form>
                </> :

              <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center mb-6">
                    <Icon name="CheckCircleIcon" size={40} className="text-green-500" variant="solid" />
                  </div>
                  <h3 className="font-heading font-black text-2xl text-navy mb-3">
                    Enquiry Received! 已收到詢價
                  </h3>
                  <p className="text-text-muted leading-relaxed max-w-md mb-8">
                    Thank you, <strong className="text-navy">{formData.contactName}</strong>! We've received your enquiry from{" "}
                    <strong className="text-navy">{formData.companyName}</strong>. Our team will respond within 24 hours with a detailed quotation.
                  </p>
                  <a
                  href="https://wa.me/85259773386"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-8 py-3.5 rounded-full font-black text-sm transition-all">
                  
                    <Icon name="ChatBubbleLeftRightIcon" size={16} variant="solid" />
                    Follow up on WhatsApp
                  </a>
                  <button
                  onClick={() => setSubmitted(false)}
                  className="mt-4 text-sm text-text-muted hover:text-navy transition-colors">
                  
                    Submit another enquiry
                  </button>
                </div>
              }
            </div>
          </div>
        </div>
      </div>
    </section>);

}